import AgGridReact from 'ui/AgGridReact/AgGridReact';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GetRowIdParams } from 'ag-grid-community';
import { useParams } from 'react-router-dom';
import Loading from 'components/Loading';
import classNames from 'classnames';
import LinkCellRenderer from 'routes/BudgetModule/components/BudgetProposal/BudgetVentilated/components/LinkCellRenderer';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { BudgetExpenseOrIncome } from 'business/constants';
import { getColumns } from './colDefinition';
import { datesFromPayload } from '../helper';
import { useOperationsQuery } from './hooks';
import { ApT } from '../types';
import { addUnaffectedBudget } from './helper';
import { useLocalStorageAGGridProps } from '../hooks';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

const getRowId = (params: GetRowIdParams) => params.data.id;

const StyApOperationGrid = styled(Box, {
    name: 'StyApOperationGrid',
})(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '2.5rem',
    background: theme.palette.lightGrey,
    '&.opGrid .ag-layout-auto-height .ag-center-cols-viewport': {
        minHeight: 'unset',
    },
    '&.opGrid ag-header': {
        borderBottom: 'none',
        borderBottomStyle: 'none',
        background: theme.palette.lightGrey,
    },
    '&.opGrid .ag-header-cell': {
        borderColor: 'white',
        borderWidth: '1px',
        borderStyle: 'solid',
    },
    '&.opGrid .ad-pinned-left-header': {
        borderRight: 'none',
    },
    '&.opGrid span.ag-header-cell-text': {
        color: theme.palette.darkGrey,
        fontSize: '9px',
    },
    '&.opGrid .ag-center-cols-viewport': {
        background: theme.palette.lightGrey,
    },
    '&.opGrid .ag-header-viewport': {
        background: theme.palette.lightGrey,
    },
    '&.opGrid .ag-header-row-column': {
        background: theme.palette.lightGrey,
    },
    '&.opGrid .ag-cell': {
        background: theme.palette.lightGrey,
        borderColor: 'white',
        borderWidth: '1px',
        borderStyle: 'solid',
    },
    '&.opGrid .ag-row': {
        borderBottomStyle: 'none',
    },
}));

export default function ApOperationGrid({
    data,
    targetYear,
}: {
    data: ApT;
    targetYear: number;
}): React.ReactElement {
    const { expenseOrIncome } = useParams();
    const apId = data.id;
    const { t } = useTranslation();
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const { data: responseData, isLoading: areOperationsLoading } =
        useOperationsQuery({
            year: targetYear,
            expenseOrIncome: expenseOrIncome ?? BudgetExpenseOrIncome.EXPENSE,
            apId,
        });

    const columns = useMemo(
        () =>
            !areOperationsLoading
                ? getColumns(
                      responseData && responseData?.length > 0
                          ? datesFromPayload(responseData[0])
                          : [],
                      t,
                      targetYear,
                      sourceTable,
                  )
                : [],
        [targetYear, areOperationsLoading],
    );

    const [columnDefs, extraProps] = useLocalStorageAGGridProps(
        `${targetYear}_OP`,
        getRowId,
        columns,
    );

    const dataToDisplay =
        !areOperationsLoading && !!responseData
            ? addUnaffectedBudget(responseData, data)
            : [];

    return (
        <StyApOperationGrid
            className={classNames('opGrid', 'ag-theme-material')}
        >
            {areOperationsLoading && <Loading />}
            {!areOperationsLoading && dataToDisplay.length === 0 && (
                <span>
                    {t('viewAllAps.noData', {
                        ressource_type: t('budgetProposal.operation'),
                    })}
                </span>
            )}
            {!areOperationsLoading && dataToDisplay.length !== 0 && (
                <AgGridReact
                    domLayout="autoHeight"
                    rowHeight={36}
                    headerHeight={30}
                    rowStyle={{
                        display: 'flex',
                    }}
                    rowData={dataToDisplay}
                    components={{
                        linkCellRenderer: LinkCellRenderer,
                    }}
                    overlayNoRowsTemplate={t('viewAllAps.noData', {
                        ressource_type: t('budgetProposal.operation'),
                    })}
                    columnDefs={columnDefs}
                    {...extraProps}
                />
            )}
        </StyApOperationGrid>
    );
}
